import React, { useState, useEffect } from "react";
import { SegmentModel } from "../EditSegment";

export enum Operator {
  Equal = 0,
  EqualTo = 0,
  Lower,
  Greater,
  Contains,
  DoesntContain,
  NotEqual,
  " value missing " = 6,
  Between = 6,
  Empty,
  NotEmpty,
  Dynamic = 9,
  Includes = 9,
  NotIncludes
}

export type CriterionModel = {
  key: string;
  highlight: boolean;
  Field: number;
  Operator: Operator;
  Value: string;
  IDs?: string;
  Data: number;
  ConditionOR: boolean;
  ActivityTypes?: string;
  amount?: string;
  Order?: number | null;
};

export enum CriterionType {
  Client = -1,
  Invoice = -2,
  ActivityType = -3,
  Number_Amount = 3,
  Date = 2,
  List = 1,
  ListInclude = 5
}

type ICriteriaContext = {
  criteria: CriterionModel[];
  addCriterion: (criterion: CriterionModel) => void;
  updateCriterion: <T extends keyof CriterionModel>(
    id: string,
    key: T,
    value: CriterionModel[T]
  ) => void;
  deleteCriterion: (id: string) => void;
  reorderCriteria: (newOrder: CriterionModel[]) => void;
};

const CriteriaContext = React.createContext({} as ICriteriaContext);

const CriteriaProvider = ({
  children,
  data,
  setData
}: React.PropsWithChildren<{
  data: SegmentModel;
  setData: React.Dispatch<React.SetStateAction<SegmentModel>>;
}>) => {
  const initializeCriteria = (criteria: CriterionModel[]): CriterionModel[] => {
    if (!criteria || criteria.length === 0) return [];
    // Ordenar por Order si está definido
    return [...criteria].sort((a, b) => {
      const orderA = a.Order ?? 0;
      const orderB = b.Order ?? 0;
      return orderA - orderB;
    });
  };
  const [criteria, setCriteria] = useState<CriterionModel[]>(
    initializeCriteria(data.Criteria ? data.Criteria : [])
  );

  useEffect(() => {
    if (data.Criteria) {
      const sorted = initializeCriteria(data.Criteria);
      setCriteria(sorted);
    }
  }, [data.Criteria]);

  const updateAllCriteria = (newCriteria: CriterionModel[]) => {
    setData((x) => ({ ...x, Criteria: newCriteria }));
    setCriteria(newCriteria);
  };

  const addCriterion = (criterion: CriterionModel) => {
    updateAllCriteria(criteria.concat(criterion));
  };

  const updateCriterion = <T extends keyof CriterionModel>(
    id: string,
    key: T,
    value: CriterionModel[T]
  ) => {
    const newCriteria = [...criteria];
    newCriteria.forEach((element: any) => {
      if (element.key === id) {
        element[key] = value;
      }
    });
    updateAllCriteria(newCriteria);
  };

  const deleteCriterion = (id: string) => {
    const newCriteria = criteria.filter((item: any) => item.key !== id);
    if (newCriteria.length === 1) {
      newCriteria[0].ConditionOR = false;
    }
    updateAllCriteria(newCriteria);
  };

  const reorderCriteria = (newOrder: CriterionModel[]) => {
    const reorderedWithOrder = newOrder.map((criterion, index) => ({
      ...criterion,
      Order: index
    }));
    updateAllCriteria(reorderedWithOrder);
  };

  const value: ICriteriaContext = {
    criteria,
    addCriterion,
    updateCriterion,
    deleteCriterion,
    reorderCriteria
  };
  return (
    <CriteriaContext.Provider value={value}>
      {children}
    </CriteriaContext.Provider>
  );
};

export { CriteriaProvider };
export default CriteriaContext;
